// /manufacturing-orders/new и /manufacturing-orders/:id/edit — форма заказа
// на производство.
//
// Общие поля (артикул, количество, заказчик, дата, комментарий, целевые
// показатели) — у любого заказа.  Остальное строится по описанию заказа
// выбранного техпроцесса (GET /manufacturing-orders/processes): атрибуты,
// слоты материалов, нумерация изделий.  Расчётные атрибуты считает сервер
// (POST /manufacturing-orders/preview) — форма их только показывает.

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { adminInventoryApi, adminMetricsApi, ApiError, manufacturingOrdersApi } from "@/lib/api";
import { localized } from "@/lib/localized";
import type {
  InventoryBatch,
  InventoryMaterial,
  ManufacturingOrderCreate,
  ManufacturingOrderUpdate,
  OrderAttributeDef,
  OrderProcess,
  UUID,
} from "@/lib/types";

const INPUT_CLS = "w-full px-3 py-2 rounded-md bg-surface border border-border";

type WithCategory = InventoryMaterial & { material_category?: string | null };

function todayIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Значение атрибута в поле формы: строка для ввода, boolean для флажка. */
type AttrValue = string | boolean;

interface FormState {
  code: string;
  process_key: string;
  article: string;
  customer_code: string;
  order_date: string;
  element_count: string;
  attributes: Record<string, AttrValue>;
  materials: Record<string, { material_id: string; batch_id: string }>;
  /** Внешние серийники, по одному в строке. */
  serials_text: string;
  comment: string;
  target_metrics: Record<string, string>;
}

function parseSerials(text: string): string[] {
  return text
    .split("\n")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** Поле формы → значение атрибута для API (пусто → null). */
function attrToApi(def: OrderAttributeDef, v: AttrValue | undefined): unknown {
  if (def.type === "boolean") return v === true;
  if (v === undefined || v === "" || typeof v === "boolean") return null;
  if (def.type === "number" || def.type === "integer") return Number(v);
  return v;
}

function attrToForm(def: OrderAttributeDef, v: unknown): AttrValue {
  if (def.type === "boolean") return v === true;
  return v == null ? "" : String(v);
}

/** Пример кода заказа по маске техпроцесса на сегодня (номер дня — N). */
function codePreview(mask: string | undefined): string {
  const d = new Date();
  const parts: Record<string, string> = {
    YYYY: String(d.getFullYear()),
    YY: String(d.getFullYear()).slice(2),
    MM: String(d.getMonth() + 1).padStart(2, "0"),
    DD: String(d.getDate()).padStart(2, "0"),
  };
  return (mask ?? "MO-{YY}{MM}{DD}{NN}").replace(/\{([^}]+)\}/g, (_a, tok: string) =>
    /^N+$/.test(tok) ? tok : (parts[tok] ?? `{${tok}}`),
  );
}

// Транслитерация кириллицы для авто-предложения латинского `key` метрики.
const TRANSLIT: Record<string, string> = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "e", ж: "zh",
  з: "z", и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o",
  п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "h", ц: "ts",
  ч: "ch", ш: "sh", щ: "sch", ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
};

function suggestMetricKey(nameRu: string): string {
  return nameRu
    .toLowerCase()
    .split("")
    .map((ch) => (ch in TRANSLIT ? TRANSLIT[ch] : ch))
    .join("")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}

export default function ManufacturingOrderForm() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const processes = useQuery({
    queryKey: ["manufacturing-orders", "processes"],
    queryFn: () => manufacturingOrdersApi.processes(),
    staleTime: 60_000,
  });

  return (
    <RequirePermission codes={["manufacturing_order.create", "manufacturing_order.update"]}>
      {processes.isLoading ? (
        <LoadingState />
      ) : processes.isError ? (
        <ErrorState error={processes.error} />
      ) : id ? (
        <EditForm id={id} processes={processes.data!} />
      ) : (
        <FormInner
          mode="create"
          processes={processes.data!}
          initialProcess={params.get("process") ?? processes.data![0]?.key ?? ""}
        />
      )}
    </RequirePermission>
  );
}

function EditForm({ id, processes }: { id: UUID; processes: OrderProcess[] }) {
  const order = useQuery({
    queryKey: ["manufacturing-order", id, "for-edit"],
    queryFn: () => manufacturingOrdersApi.get(id),
  });

  if (order.isLoading) return <LoadingState />;
  if (order.isError) return <ErrorState error={order.error} />;

  const o = order.data!;
  const process = processes.find((p) => p.key === o.process_key);
  const initial: FormState = {
    code: o.code,
    process_key: o.process_key ?? "",
    article: o.article ?? "",
    customer_code: o.customer_code ?? "",
    order_date: o.order_date ?? todayIso(),
    element_count: o.element_count != null ? String(o.element_count) : "",
    attributes: Object.fromEntries(
      (process?.order.attributes ?? []).map((d) => [d.key, attrToForm(d, o.attributes[d.key])]),
    ),
    materials: Object.fromEntries(
      o.materials.map((m) => [m.slot, { material_id: m.material_id ?? "", batch_id: m.batch_id ?? "" }]),
    ),
    serials_text: o.external_serials.map((s) => s.external_serial).join("\n"),
    comment: o.comment ?? "",
    target_metrics: Object.fromEntries(
      Object.entries(o.target_metrics ?? {}).map(([k, v]) => [k, String(v ?? "")]),
    ),
  };

  return (
    <FormInner
      mode="edit"
      id={id}
      processes={processes}
      initial={initial}
      initialProcess={initial.process_key}
      launched={o.status === "IN_PRODUCTION" || o.status === "COMPLETED"}
      processLocked={o.status !== "DRAFT"}
    />
  );
}

function FormInner({
  mode,
  id,
  processes,
  initial,
  initialProcess,
  launched,
  processLocked,
}: {
  mode: "create" | "edit";
  id?: UUID;
  processes: OrderProcess[];
  initial?: FormState;
  initialProcess: string;
  /** Заказ запущен: общие ключевые поля и помеченные атрибуты/слоты заблокированы. */
  launched?: boolean;
  /** Техпроцесс меняется только в черновике. */
  processLocked?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(
    initial ?? {
      code: "",
      process_key: initialProcess,
      article: "",
      customer_code: "",
      order_date: todayIso(),
      element_count: "",
      attributes: {},
      materials: {},
      serials_text: "",
      comment: "",
      target_metrics: {},
    },
  );
  const [err, setErr] = useState<string | null>(null);

  const process = processes.find((p) => p.key === form.process_key) ?? null;
  const cfg = process?.order;
  const serialMode = cfg?.serials.mode ?? "none";
  const editableAttrs = (cfg?.attributes ?? []).filter((d) => !d.computed);
  const computedAttrs = (cfg?.attributes ?? []).filter((d) => d.computed);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }
  function setAttr(key: string, value: AttrValue) {
    setForm((f) => ({ ...f, attributes: { ...f.attributes, [key]: value } }));
  }
  function setSlot(slot: string, patch: Partial<{ material_id: string; batch_id: string }>) {
    setForm((f) => {
      const cur = f.materials[slot] ?? { material_id: "", batch_id: "" };
      const next = { ...cur, ...patch };
      // Сменился материал — партия прежнего материала не подходит.
      if (patch.material_id !== undefined && patch.material_id !== cur.material_id) next.batch_id = "";
      return { ...f, materials: { ...f.materials, [slot]: next } };
    });
  }

  // ── Материалы для слотов ────────────────────────────────────────────
  const materials = useQuery({
    queryKey: ["admin", "inventory", "materials", { active: true }],
    queryFn: () => adminInventoryApi.listMaterials({ active: true }),
    staleTime: 60_000,
    enabled: (cfg?.materials.length ?? 0) > 0,
  });
  const allMaterials = (materials.data?.items ?? []) as WithCategory[];
  const materialsFor = (category: string | undefined) =>
    category ? allMaterials.filter((m) => !m.material_category || m.material_category === category) : allMaterials;

  // ── Целевые показатели (метрики изделия) ────────────────────────────
  const metrics = useQuery({
    queryKey: ["admin", "metrics", "product"],
    queryFn: () => adminMetricsApi.list("product"),
    staleTime: 60_000,
  });
  const metricDefs = metrics.data?.items ?? [];
  const [newMetric, setNewMetric] = useState({ name_ru: "", key: "", unit: "" });
  const [keyTouched, setKeyTouched] = useState(false);
  const [metricErr, setMetricErr] = useState<string | null>(null);
  const newMetricKey = keyTouched ? newMetric.key : suggestMetricKey(newMetric.name_ru);
  const addMetric = useMutation({
    mutationFn: () =>
      adminMetricsApi.create({
        scope: "product",
        value_type: "number",
        name_ru: newMetric.name_ru.trim(),
        key: newMetricKey.trim(),
        unit: newMetric.unit.trim() || null,
        sort_order: metricDefs.length,
      }),
    onSuccess: (created) => {
      setNewMetric({ name_ru: "", key: "", unit: "" });
      setKeyTouched(false);
      setMetricErr(null);
      void qc.invalidateQueries({ queryKey: ["admin", "metrics", "product"] });
      set("target_metrics", { ...form.target_metrics, [created.key]: "" });
    },
    onError: (e) =>
      setMetricErr(
        e instanceof ApiError && e.status === 409
          ? t("mo.form.metricExists")
          : e instanceof Error
            ? e.message
            : t("mo.form.metricFailed"),
      ),
  });

  // ── Предпросмотр расчётных атрибутов (сервер) ───────────────────────
  const elementCount =
    serialMode === "external" ? parseSerials(form.serials_text).length : Number(form.element_count) || null;
  const previewInput = useMemo(() => {
    if (!cfg?.handler || computedAttrs.length === 0) return null;
    const attributes: Record<string, unknown> = {};
    for (const d of editableAttrs) {
      const v = attrToApi(d, form.attributes[d.key]);
      if (v != null && !(typeof v === "number" && !Number.isFinite(v))) attributes[d.key] = v;
    }
    return { process_key: form.process_key, element_count: elementCount, attributes };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.process_key, form.attributes, elementCount, cfg?.handler]);
  const preview = useQuery({
    queryKey: ["manufacturing-orders", "preview", previewInput],
    queryFn: () => manufacturingOrdersApi.preview(previewInput!),
    enabled: !!previewInput,
    staleTime: 10_000,
  });

  // ── Сохранение ──────────────────────────────────────────────────────
  const save = useMutation({
    mutationFn: async () => {
      const target_metrics: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(form.target_metrics)) {
        if (v === "" || v == null) continue;
        const num = Number(v);
        target_metrics[k] = Number.isNaN(num) ? v : num;
      }
      const attributes: Record<string, unknown> = {};
      for (const d of editableAttrs) {
        if (launched && d.locked_after_launch) continue;
        attributes[d.key] = attrToApi(d, form.attributes[d.key]);
      }
      const slots = (cfg?.materials ?? [])
        .filter((d) => !(launched && d.locked_after_launch))
        .map((d) => {
          const m = form.materials[d.slot];
          return {
            slot: d.slot,
            material_id: m?.material_id || null,
            ...(d.batch ? { batch_id: m?.batch_id || null } : {}),
          };
        });
      const numbering =
        serialMode === "external"
          ? { external_serials: parseSerials(form.serials_text) }
          : { element_count: form.element_count ? Number(form.element_count) : null };

      if (mode === "create") {
        const body: ManufacturingOrderCreate = {
          source_system: "mes",
          process_key: form.process_key,
          article: form.article.trim() || null,
          customer_code: form.customer_code.trim() || null,
          order_date: form.order_date || null,
          attributes,
          materials: slots.filter((s) => s.material_id || ("batch_id" in s && s.batch_id)),
          comment: form.comment.trim() || null,
          target_metrics,
          ...numbering,
        };
        return manufacturingOrdersApi.create(body);
      }
      const patch: ManufacturingOrderUpdate = {
        comment: form.comment.trim() || null,
        target_metrics,
        attributes,
        materials: slots,
        ...(launched
          ? {}
          : {
              ...(processLocked ? {} : { process_key: form.process_key }),
              article: form.article.trim() || null,
              customer_code: form.customer_code.trim() || null,
              order_date: form.order_date || null,
              ...numbering,
            }),
      };
      return manufacturingOrdersApi.update(id!, patch);
    },
    onSuccess: (saved) => {
      qc.invalidateQueries({ queryKey: ["manufacturing-orders"] });
      qc.invalidateQueries({ queryKey: ["manufacturing-order", saved.id] });
      navigate(`/manufacturing-orders/${saved.id}`);
    },
    onError: (e) =>
      setErr(e instanceof ApiError ? `${e.code}: ${e.message}` : e instanceof Error ? e.message : t("mo.form.saveFailed")),
  });

  if (!process) {
    return <ErrorState error={new Error(t("mo.form.noProcess"))} />;
  }

  return (
    <>
      <PageHeader
        eyebrow={t("mo.eyebrow")}
        title={mode === "create" ? t("mo.form.titleNew") : t("mo.form.titleEdit")}
        description={
          mode === "create"
            ? t("mo.form.descNew")
            : launched
              ? t("mo.form.descLaunched")
              : t("mo.form.descDraft")
        }
        actions={
          <button className="btn" onClick={() => navigate(id ? `/manufacturing-orders/${id}` : "/manufacturing-orders")}>
            {t("common.cancel")}
          </button>
        }
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          save.mutate();
        }}
        className="space-y-6 mt-2"
      >
        <section className="card p-5 space-y-4">
          <h2 className="text-lg">{t("mo.form.document")}</h2>

          {processes.length > 1 && (
            <Field label={t("mo.form.process")}>
              <div className="flex flex-wrap gap-2">
                {processes.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => set("process_key", p.key)}
                    disabled={processLocked}
                    className={`px-3 py-2 rounded-md border text-sm ${
                      form.process_key === p.key
                        ? "border-accent bg-accent/10 text-accent-strong"
                        : "border-border bg-surface text-text-secondary"
                    } ${processLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
              {processLocked && <p className="text-xs text-text-muted mt-1">{t("mo.form.processLocked")}</p>}
            </Field>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t("mo.form.code")}>
              {mode === "edit" ? (
                <input className={`${INPUT_CLS} font-mono`} value={form.code} disabled />
              ) : (
                <>
                  <div className={`${INPUT_CLS} font-mono flex items-center text-text-muted`}>
                    {codePreview(cfg?.code_mask)}
                  </div>
                  <p className="text-xs text-text-muted mt-1">{t("mo.form.codeAuto")}</p>
                </>
              )}
            </Field>
            <Field label={t("mo.form.orderDate")}>
              <input
                type="date"
                className={INPUT_CLS}
                value={form.order_date}
                onChange={(e) => set("order_date", e.target.value)}
                disabled={launched}
              />
            </Field>
          </div>
        </section>

        <section className="card p-5 space-y-4">
          <h2 className="text-lg">{t("mo.form.product")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label={t("mo.fields.article")}>
              <input
                className={`${INPUT_CLS} font-mono`}
                value={form.article}
                onChange={(e) => set("article", e.target.value)}
                disabled={launched}
              />
            </Field>
            <Field label={t("mo.fields.customerCode")}>
              <input
                className={`${INPUT_CLS} font-mono uppercase`}
                value={form.customer_code}
                onChange={(e) => set("customer_code", e.target.value.toUpperCase())}
                maxLength={10}
                disabled={launched}
              />
            </Field>
            {serialMode !== "external" && (
              <Field label={t("mo.fields.elementCount")}>
                <input
                  type="number"
                  min={1}
                  step={1}
                  className={`${INPUT_CLS} font-mono tabular-nums`}
                  value={form.element_count}
                  onChange={(e) => set("element_count", e.target.value)}
                  disabled={launched}
                />
              </Field>
            )}
          </div>
          {serialMode === "mask" && cfg?.serials.mask && (
            <p className="text-xs text-text-muted">
              {t("mo.form.serialMask")} <span className="font-mono">{cfg.serials.mask}</span>
            </p>
          )}

          {editableAttrs.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {editableAttrs.map((d) => (
                <AttributeField
                  key={d.key}
                  def={d}
                  value={form.attributes[d.key] ?? (d.type === "boolean" ? d.default === true : "")}
                  onChange={(v) => setAttr(d.key, v)}
                  disabled={!!launched && !!d.locked_after_launch}
                />
              ))}
            </div>
          )}

          {computedAttrs.length > 0 && (
            <div className="rounded-md border border-border bg-surface-alt/40 p-4 flex flex-wrap gap-8">
              {computedAttrs.map((d) => (
                <Computed
                  key={d.key}
                  label={`${localized(d.label, d.key)}${d.unit ? `, ${d.unit}` : ""}`}
                  value={(preview.data?.attributes[d.key] as number | null | undefined) ?? null}
                  pending={preview.isFetching && !preview.data}
                />
              ))}
              <p className="text-xs text-text-muted self-end max-w-xs">{t("mo.form.computedHint")}</p>
            </div>
          )}
        </section>

        {serialMode === "external" && (
          <section className="card p-5 space-y-4">
            <h2 className="text-lg">{t("mo.form.externalSerials")}</h2>
            <Field label={t("mo.form.externalSerialsHint")}>
              <textarea
                className={`${INPUT_CLS} font-mono`}
                rows={8}
                value={form.serials_text}
                onChange={(e) => set("serials_text", e.target.value)}
                disabled={processLocked}
                placeholder={"SN-0001\nSN-0002"}
              />
            </Field>
            <p className="text-sm text-text-secondary">
              {t("mo.form.externalSerialsCount")}{" "}
              <span className="font-mono tabular-nums">{parseSerials(form.serials_text).length}</span>
            </p>
          </section>
        )}

        {(cfg?.materials.length ?? 0) > 0 && (
          <section className="card p-5 space-y-4">
            <h2 className="text-lg">{t("mo.form.materials")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {cfg!.materials.map((d) => {
                const m = form.materials[d.slot] ?? { material_id: "", batch_id: "" };
                const locked = !!launched && !!d.locked_after_launch;
                return (
                  <div key={d.slot} className="space-y-2">
                    <Field label={localized(d.label, d.slot)} required={d.required_for_ready}>
                      <select
                        className={`${INPUT_CLS} font-mono`}
                        value={m.material_id}
                        onChange={(e) => setSlot(d.slot, { material_id: e.target.value })}
                        disabled={locked}
                      >
                        <option value="">{t("mo.form.notSelected")}</option>
                        {materialsFor(d.category).map((mat) => (
                          <option key={mat.id} value={mat.id}>
                            {mat.code} · {mat.name}
                          </option>
                        ))}
                      </select>
                    </Field>
                    {d.batch && (
                      <BatchSelect
                        materialId={m.material_id}
                        value={m.batch_id}
                        onChange={(v) => setSlot(d.slot, { batch_id: v })}
                        label={t("mo.form.batch")}
                        emptyLabel={t("mo.form.notSelected")}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        <section className="card p-5 space-y-4">
          <h2 className="text-lg">{t("mo.form.targets")}</h2>
          {metrics.isLoading ? (
            <p className="text-sm text-text-muted">{t("common.loading")}</p>
          ) : metricDefs.length === 0 ? (
            <p className="text-sm text-text-muted">{t("mo.form.noMetrics")}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {metricDefs.map((md) => (
                <Field key={md.id} label={`${md.name_ru}${md.unit ? `, ${md.unit}` : ""}`}>
                  <input
                    className={`${INPUT_CLS} font-mono tabular-nums`}
                    value={form.target_metrics[md.key] ?? ""}
                    onChange={(e) => set("target_metrics", { ...form.target_metrics, [md.key]: e.target.value })}
                    placeholder={md.key}
                  />
                </Field>
              ))}
            </div>
          )}
          <div className="rounded-md border border-dashed border-border bg-surface-alt/40 p-4 space-y-3">
            <p className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("mo.form.addMetric")}</p>
            <div className="grid grid-cols-1 md:grid-cols-[2fr_1.5fr_1fr_auto] gap-3 items-end">
              <Field label={t("mo.form.metricName")}>
                <input
                  className={INPUT_CLS}
                  value={newMetric.name_ru}
                  onChange={(e) => setNewMetric((m) => ({ ...m, name_ru: e.target.value }))}
                />
              </Field>
              <Field label={t("mo.form.metricKey")}>
                <input
                  className={`${INPUT_CLS} font-mono`}
                  value={newMetricKey}
                  onChange={(e) => {
                    setKeyTouched(true);
                    setNewMetric((m) => ({ ...m, key: e.target.value }));
                  }}
                />
              </Field>
              <Field label={t("mo.form.metricUnit")}>
                <input
                  className={INPUT_CLS}
                  value={newMetric.unit}
                  onChange={(e) => setNewMetric((m) => ({ ...m, unit: e.target.value }))}
                />
              </Field>
              <button
                type="button"
                className="btn btn-secondary h-[42px] whitespace-nowrap"
                disabled={addMetric.isPending || newMetric.name_ru.trim() === "" || newMetricKey.trim() === ""}
                onClick={() => addMetric.mutate()}
              >
                {addMetric.isPending ? t("common.adding") : `+ ${t("common.add")}`}
              </button>
            </div>
            {metricErr && <p className="text-sm text-danger">{metricErr}</p>}
          </div>
        </section>

        <section className="card p-5 space-y-4">
          <h2 className="text-lg">{t("mo.fields.comment")}</h2>
          <textarea
            className={INPUT_CLS}
            rows={3}
            value={form.comment}
            onChange={(e) => set("comment", e.target.value)}
          />
        </section>

        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="btn"
            onClick={() => navigate(id ? `/manufacturing-orders/${id}` : "/manufacturing-orders")}
          >
            {t("common.cancel")}
          </button>
          <button type="submit" className="btn btn-primary" disabled={save.isPending}>
            {save.isPending ? t("common.saving") : mode === "create" ? t("mo.form.create") : t("common.save")}
          </button>
        </div>
      </form>
    </>
  );
}

function AttributeField({
  def,
  value,
  onChange,
  disabled,
}: {
  def: OrderAttributeDef;
  value: AttrValue;
  onChange: (v: AttrValue) => void;
  disabled: boolean;
}) {
  const label = `${localized(def.label, def.key)}${def.unit ? `, ${def.unit}` : ""}`;
  if (def.type === "boolean") {
    return (
      <label className="flex items-center gap-2 text-sm self-end pb-2">
        <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
        {label}
      </label>
    );
  }
  const numeric = def.type === "number" || def.type === "integer";
  return (
    <Field label={label} required={def.required_for_ready}>
      <input
        type={def.type === "date" ? "date" : numeric ? "number" : "text"}
        step={def.type === "integer" ? 1 : numeric ? "any" : undefined}
        className={`${INPUT_CLS} ${numeric ? "font-mono tabular-nums" : ""}`}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      />
    </Field>
  );
}

function BatchSelect({
  materialId,
  value,
  onChange,
  label,
  emptyLabel,
}: {
  materialId: string;
  value: string;
  onChange: (v: string) => void;
  label: string;
  emptyLabel: string;
}) {
  const batches = useQuery({
    queryKey: ["admin", "inventory", "batches", materialId],
    queryFn: () => adminInventoryApi.listBatches({ material_id: materialId }),
    enabled: !!materialId,
  });
  return (
    <Field label={label}>
      <select
        className={`${INPUT_CLS} font-mono`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!materialId}
      >
        <option value="">{emptyLabel}</option>
        {(batches.data?.items ?? []).map((b: InventoryBatch) => (
          <option key={b.id} value={b.id}>
            {b.batch_no}
          </option>
        ))}
      </select>
    </Field>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
        {label}
        {required && <span className="text-danger ml-1">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Computed({ label, value, pending }: { label: string; value: number | null; pending?: boolean }) {
  return (
    <div>
      <p className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</p>
      <p className="text-2xl font-mono tabular-nums mt-1">{pending ? "…" : value != null ? value : "—"}</p>
    </div>
  );
}
